import { ok } from 'neverthrow';
import type {
  ActionResult,
  ApplicableContext,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { WorkflowEditorToolbarActionBase } from './workflow-editor-toolbar-action.base';
import { selectionHasDeletableItems } from './workflow-toolbar-action.helpers';

type WorkflowDeleteSelectionOps = {
  /**
   * Delete whatever is selected on the canvas, via React Flow's `deleteElements`.
   *
   * Deliberately not the individual `deleteWorkflowNode` / `deleteWorkflowEdge` /
   * `archiveTriggerBinding` mutations this action used to call itself. Going
   * through React Flow means the button and the Backspace/Delete key take one
   * path: both hit the canvas's `onBeforeDelete` (so one confirmation covers
   * both) and both land in the canvas change handlers, which already know to
   * archive a trigger's binding rather than delete it and to skip synthetic
   * edges. That logic used to exist twice.
   */
  deleteSelection: () => Promise<void>;
};

export class WorkflowToolbarDeleteSelectionAction extends WorkflowEditorToolbarActionBase {
  title = 'Delete';
  override description = 'Delete the selected nodes or edges';
  group = 'Workflow';
  icon = 'trash';

  constructor(private readonly ops: WorkflowDeleteSelectionOps) {
    super();
  }

  override applicable(context: ApplicableContext): boolean {
    return selectionHasDeletableItems(context);
  }

  override async execute(
    _params: Record<string, never>,
    _context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    await this.ops.deleteSelection();
    return ok({});
  }
}
